import { useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faChevronDown, faBolt, faStar, faTruck, faTag, faComments, faBuilding, faEnvelope, faShieldHalved, faHelmetSafety, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons'
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons'
import {
  getStoreContactEmail,
  getStoreContactPhone,
  getStorefrontConfig,
  listStorefrontTestimonials,
  toExternalImage,
} from '../lib/dropify'
import { absoluteUrl, brandTitle, buildFaqSchema, DEFAULT_OG_IMAGE } from '../lib/seo'
import type { FaqItem } from '../lib/seo'
import { whatsAppCtaProps, track } from '../lib/analytics'
import { listDashboardNewsPosts } from '../lib/news'
import { BrandTrademark, SocialLinks } from '../components/SiteChrome'
import { WhatsAppChannelSection } from '../components/WhatsAppChannelSection'
import { LpCrossLinks } from '../components/LpCrossLinks'
import { AdNewsProofSection } from '../components/HomeNewsSection'

const WA_NUMBER = '6285704011028'
const LP_WALL_COUNT = 24
const LP_WALL_IMAGE_TRANSFORM = { width: 380, format: 'webp', quality: 55 } as const

// Hand-picked product photos for this gloves landing page. Static assets in
// /public/lp — this page is intentionally not driven by a storefront category.
const PRODUCT_NAME = 'Sarung Tangan'
const HERO_IMAGE = '/lp/sarung-tangan-gekko-3.jpeg'

function waLink(message: string) {
  return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(message)}`
}

// The testimonials API returns proof photos (chat/order screenshots) with no
// per-person text, so the "wall of proof" is built from the real images.
function extractTestimonialImages(raw: unknown): string[] {
  const list = Array.isArray(raw)
    ? raw
    : raw && typeof raw === 'object' && Array.isArray((raw as Record<string, unknown>).items)
      ? (raw as { items: unknown[] }).items
      : []
  const urls: string[] = []
  for (const entry of list) {
    if (!entry || typeof entry !== 'object') continue
    const media = (entry as Record<string, unknown>).media_items
    if (!Array.isArray(media)) continue
    for (const item of media) {
      if (!item || typeof item !== 'object') continue
      const record = item as Record<string, unknown>
      const isImage = record.media_type === 'image' || record.media_type == null
      const url = typeof record.url === 'string' ? record.url : typeof record.storage_url === 'string' ? record.storage_url : null
      if (isImage && url) urls.push(url)
    }
  }
  return urls
}

// Casual, gloves-tailored FAQ — friendlier than the SEO-leaning category-page
// copy, and written to reassure a hesitant first-time buyer.
const FAQ_ITEMS: FaqItem[] = [
  {
    q: 'Berapa minimum order sarung tangan?',
    a: 'Minimum order mengikuti jenis sarung tangan. Buka ketentuan minimum order, lalu kirim nama produk dan jumlah yang direncanakan agar admin dapat memeriksa satuan pembeliannya.',
    link: { href: '/minimum-order', label: 'Lihat detail minimum order per kategori →' },
  },
  {
    q: 'Bagaimana cara meminta harga grosir sarung tangan?',
    a: 'Kirim nama atau jenis produk, jumlah kebutuhan, dan kota tujuan. Admin akan memeriksa stok lalu mengirim harga dan ongkir dalam quotation.',
  },
  {
    q: 'Jenis sarung tangan apa yang tersedia?',
    a: 'Katalog mencakup sarung tangan katun rajut 3–7 benang, sarung tangan berbintik PVC, dan Gekko Grip berlapis Nitrile atau PU. Sampaikan jenis pekerjaan dan benda yang dipegang agar admin dapat membantu mempersempit pilihan.',
  },
  {
    q: 'Apakah jenis dan warna dapat dicampur?',
    a: 'Ketentuannya mengikuti produk dan susunan stok. Kirim pembagian jenis atau warna yang dibutuhkan agar admin dapat memeriksanya sebelum quotation dibuat.',
  },
  {
    q: 'Apakah saya dapat meminta sampel lebih dulu?',
    a: 'Ketersediaan sampel perlu diperiksa per produk. Total biaya sampel terdiri dari harga satuan sarung tangan dan ongkir ke kota tujuan.',
  },
  {
    q: 'Bagaimana proses pembayarannya?',
    a: 'Pembayaran dilakukan melalui transfer BCA atau Mandiri. Admin memeriksa bukti transfer dan mengonfirmasi pembayaran sebelum pesanan disiapkan.',
  },
  {
    q: 'Berapa lama pesanan dikirim?',
    a: 'Waktu penyiapan mengikuti isi dan jumlah pesanan. Waktu perjalanan mengikuti kota tujuan, ekspedisi, dan jenis layanan. Admin akan memberikan estimasi keduanya sebelum pesanan diproses dan mengirim nomor resi setelah paket diserahkan kepada ekspedisi.',
  },
  {
    q: 'Saya baru mulai menjual alat safety. Data apa yang perlu disiapkan?',
    a: 'Kirim jenis pelanggan, produk yang ingin dibandingkan, perkiraan jumlah, dan kota tujuan. Admin akan menjelaskan pilihan barang dan proses pemesanan. Anda dapat meminta informasi terlebih dahulu sebelum memesan.',
  },
  {
    q: 'Apakah pabrik dapat memesan sarung tangan untuk kebutuhan pegawai?',
    a: 'Bisa. Kirim jenis pekerjaan, risiko utama, jumlah pekerja, standar wajib, dan kota tujuan. Kebutuhan PO, invoice, serta jadwal pengiriman perlu disampaikan saat meminta quotation.',
    link: { href: '/sarung-tangan-pabrik', label: 'Lihat halaman supplier sarung tangan pabrik →' },
  },
]

export const Route = createFileRoute('/lp/grosir-sarung-tangan')({
  loader: async () => {
    const [config, testimonialsRaw, newsPosts] = await Promise.all([
      getStorefrontConfig().catch(() => null),
      listStorefrontTestimonials({ page: 1, per_page: 4 }).catch(() => []),
      listDashboardNewsPosts().catch(() => []),
    ])

    return {
      config,
      testimonialImages: extractTestimonialImages(testimonialsRaw).slice(0, LP_WALL_COUNT),
      newsPosts,
    }
  },
  head: () => {
    const title = brandTitle('Grosir Sarung Tangan Kerja Tangan Pertama')
    const description =
      'Lihat sarung tangan katun rajut, bintik PVC, dan seri kerja berlapis. Kirim produk, jumlah, penggunaan, dan kota tujuan untuk meminta quotation.'
    const ogImage = absoluteUrl(HERO_IMAGE)

    return {
      meta: [
        { title },
        { name: 'description', content: description },
        // Ads-only destination; the matching organic page carries this intent.
        { name: 'robots', content: 'noindex, follow' },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:url', content: absoluteUrl('/lp/grosir-sarung-tangan') },
        { property: 'og:image', content: ogImage || DEFAULT_OG_IMAGE },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: description },
        { name: 'twitter:image', content: ogImage || DEFAULT_OG_IMAGE },
      ],
      links: [
        { rel: 'canonical', href: absoluteUrl('/lp/grosir-sarung-tangan') },
      ],
      scripts: [
        {
          type: 'application/ld+json',
          children: JSON.stringify(buildFaqSchema(FAQ_ITEMS)),
        },
      ],
    }
  },
  component: LandingPage,
})

// Amber highlighter swipe behind the hook keyword — the page's signature.
function Mark({ children }: { children: ReactNode }) {
  return (
    <span className="relative whitespace-nowrap">
      <span className="relative z-10">{children}</span>
      <span aria-hidden className="absolute -inset-x-1 bottom-0.5 z-0 h-[0.5em] -rotate-1 rounded-sm bg-amber-400/80" />
    </span>
  )
}

const WHY_US_ITEMS = [
  {
    icon: faTag,
    title: 'Quotation sebelum pembayaran',
    desc: 'Harga mengikuti jenis sarung tangan dan jumlah pesanan. Admin mengirim rincian melalui quotation sebelum Anda membayar.',
  },
  {
    icon: faStar,
    title: 'Pilihan untuk toko dan proyek',
    desc: 'Katalog mencakup sarung tangan rajut, bintik PVC, serta seri berlapis Nitrile atau PU untuk kebutuhan yang berbeda.',
  },
  {
    icon: faTruck,
    title: 'Pengiriman antarkota',
    desc: 'Pesanan dikirim dari Surabaya. Admin mengirim nomor resi setelah barang diserahkan kepada ekspedisi.',
  },
  {
    icon: faComments,
    title: 'Pemesanan lewat WhatsApp',
    desc: 'Kirim jenis produk, jumlah, dan kota tujuan agar admin dapat memeriksa stok serta menyiapkan quotation.',
  },
]

const FIT_ITEMS = [
  'Toko bangunan, alat teknik, atau toko safety yang membeli sarung tangan secara grosir.',
  'Reseller yang menerima permintaan dari proyek, pabrik, bengkel, atau usaha pertanian.',
  'Toko yang perlu membandingkan sarung tangan rajut, bintik PVC, Nitrile, dan PU.',
  'Penjual baru yang memerlukan penjelasan tentang produk, satuan, dan MOQ.',
  'Pembeli yang ingin menerima quotation sebelum menghitung margin penjualan.',
  'Tim pengadaan yang perlu mencocokkan produk dengan pekerjaan dan risiko.',
]

// Hand-curated product types showcased with the real photos in /public/lp.
const PRODUCT_TYPES = [
  {
    image: '/lp/sarung-tangan-katalog.jpeg',
    title: 'Sarung Tangan Katun Rajut',
    desc: 'Tersedia dalam konstruksi 3–7 benang dengan tingkat ketebalan yang berbeda. Pilih berdasarkan pekerjaan dan benda yang ditangani.',
    tag: 'Rajut 3–7 benang',
  },
  {
    image: '/lp/sarung-tangan-gekko-spread.jpeg',
    title: 'Sarung Tangan Nitrile Gekko Grip',
    desc: 'Telapak berlapis nitrile untuk pekerjaan mekanikal dan penanganan komponen yang berpotensi terkena minyak ringan. Tersedia warna biru, hitam, dan putih-hitam.',
    tag: 'Heavy duty',
  },
  {
    image: '/lp/sarung-tangan-gekko-3.jpeg',
    title: 'Seri Gekko Grip',
    desc: 'Pilihan lapisan Nitrile dan PU untuk kondisi kerja yang berbeda. Marking serta dokumen perlu diperiksa per seri sebelum PO.',
    tag: 'Nitrile dan PU',
  },
  {
    image: '/lp/sarung-tangan-gekko-brand.jpeg',
    title: 'Sarung Tangan Bintik PVC',
    desc: 'Bintik PVC membantu cengkeraman saat menangani benda kering. Bahan, ukuran, dan ketebalan perlu diperiksa per seri.',
    tag: 'Bintik PVC',
  },
]

const OBJECTIONS = [
  {
    worry: '“Saya belum tahu produk yang perlu dibandingkan.”',
    answer: 'Kirim penggunaan, jenis produk, perkiraan jumlah, dan kota tujuan. Admin akan menjelaskan pilihan barang, MOQ, quotation, dan pengiriman.',
  },
  {
    worry: '“Saya belum tahu apakah marginnya cukup.”',
    answer: 'Minta quotation sesuai produk dan jumlah, lalu hitung margin setelah harga beli, ongkir, kemasan, biaya platform, promosi, dan retur.',
  },
  {
    worry: '“Modal awal saya terbatas.”',
    answer: 'MOQ kategori sarung tangan umumnya mulai 25 lusin. Beberapa merek atau seri dapat memiliki ketentuan berbeda, jadi konfirmasikan produk yang dipilih.',
  },
  {
    worry: '“Saya perlu memastikan stok sebelum menerima pesanan.”',
    answer: 'Kirim kode produk dan jumlah yang dibutuhkan. Admin akan mengonfirmasi ketersediaan sebelum pembayaran.',
  },
]

function WhatsAppButton({
  message,
  label,
  itemName,
  placement,
  className,
}: {
  message: string
  label: string
  // When the CTA is tied to a specific product type, pass its name so the lead
  // carries which item drove it (mirrors the catalog LP's per-product tracking).
  itemName?: string
  placement?: string
  className?: string
}) {
  return (
    <a
      href={waLink(message)}
      {...whatsAppCtaProps('landing_page', {
          cta_placement: placement ?? (itemName ? 'product_card' : 'body_primary'),
          lp_category: PRODUCT_NAME,
          ...(itemName ? { lead_item_name: itemName, items: [{ item_id: itemName, item_name: itemName, item_category: PRODUCT_NAME }] } : {}),
        })}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
    >
      <FontAwesomeIcon icon={faWhatsapp} className="w-5 h-5" />
      {label}
    </a>
  )
}

function LandingPage() {
  const { config, testimonialImages, newsPosts } = Route.useLoaderData()

  const contactPhone = getStoreContactPhone(config)
  const contactEmail = getStoreContactEmail(config)

  const heroMessage = 'Halo Dropify, saya mau kulakan sarung tangan kerja. Mohon kirim informasi produk, stok, dan cara meminta quotation.'
  const softMessage = 'Halo Dropify, saya sedang membandingkan sarung tangan kerja. Mohon jelaskan pilihan produk dan ketentuan ordernya.'
  const ctaMessage = 'Halo Dropify, saya mau mulai menjual sarung tangan kerja. Mohon kirim katalog, MOQ, dan cara order.'

  return (
    <div className="min-h-screen bg-white">
      {/* Urgency strip */}
      <div className="bg-amber-400 text-slate-900 text-center text-xs sm:text-sm font-semibold px-4 py-2">
        <FontAwesomeIcon icon={faBolt} className="w-3.5 h-3.5 mr-1.5" />
        Admin mengonfirmasi harga, stok, dan waktu penyiapan sebelum Anda memesan.
      </div>

      {/* Minimal header — logo + single CTA, no nav links to avoid click leaks */}
      <header className="border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2.5" aria-label="Dropify">
            <img src="/logo80.webp" srcSet="/logo40.webp 40w, /logo80.webp 80w" sizes="40px" alt="Dropify" width="40" height="40" className="h-10 w-10 object-contain" />
            <span className="text-lg font-black tracking-tight text-slate-900">Dropify</span>
          </a>
          <WhatsAppButton
            message={heroMessage}
            label="Hubungi admin"
            placement="header"
            className="inline-flex items-center gap-2 rounded-lg bg-green-700 hover:bg-green-800 px-4 py-2.5 text-sm font-bold text-white transition-colors"
          />
        </div>
      </header>

      <main>
      {/* Hero — hook + dual CTA */}
      <section className="bg-[#0B1120] overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 py-14 md:py-20 grid lg:grid-cols-2 gap-10 lg:gap-12 items-center">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-bold uppercase tracking-wider text-amber-300">
              Untuk reseller, toko bangunan, dan toko safety
            </span>
            <h1 className="mt-5 text-4xl md:text-5xl font-black tracking-tight text-white leading-[1.08]">
              Bandingkan sarung tangan kerja <Mark>sebelum menentukan pesanan.</Mark>
            </h1>
            <p className="mt-5 text-base md:text-lg text-slate-300 leading-relaxed">
              Pilih sarung tangan katun rajut, bintik PVC, atau seri kerja berlapis. Kirim jenis penggunaan, jumlah, dan kota tujuan agar admin dapat memeriksa produk yang sesuai.
            </p>

            <div className="mt-6 inline-flex items-baseline gap-2.5 rounded-xl border border-amber-400/30 bg-amber-400/10 px-4 py-3">
              <span className="text-xs font-bold uppercase tracking-wider text-amber-300">Satuan grosir</span>
              <span className="text-2xl md:text-3xl font-black leading-none text-white">MOQ per produk</span>
              <span className="text-sm font-medium text-slate-300">dikonfirmasi admin</span>
            </div>

            <div className="mt-7 flex flex-col sm:flex-row sm:items-center gap-3">
              <WhatsAppButton
                message={heroMessage}
                label="Minta quotation sarung tangan"
                placement="hero_primary"
                className="inline-flex items-center justify-center gap-2.5 rounded-lg bg-green-700 hover:bg-green-800 px-7 py-4 text-base font-bold text-white shadow-lg shadow-green-900/30 transition-colors"
              />
              <a
                href={waLink(softMessage)}
                {...whatsAppCtaProps('landing_page', { cta_placement: 'hero_secondary', lp_category: PRODUCT_NAME })}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-white/25 px-6 py-4 text-sm font-semibold text-white hover:bg-white/10 transition-colors"
              >
                Tanyakan perbedaan produk →
              </a>
            </div>
            <p className="mt-3 text-sm text-slate-400">Anda dapat meminta informasi produk dan MOQ sebelum menentukan pesanan.</p>
          </div>

          {/* Hero visual */}
          <div className="relative">
            <div className="relative rounded-3xl overflow-hidden ring-1 ring-white/10 shadow-2xl bg-white">
              <img src={HERO_IMAGE} alt="Grosir sarung tangan kerja Gekko Grip Dropify" width="800" height="800" loading="eager" fetchPriority="high" decoding="async" className="w-full aspect-square object-cover" />
            </div>
            <div className="absolute -bottom-4 -left-3 sm:left-4 flex items-center gap-2 rounded-xl bg-white px-4 py-3 shadow-xl">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-400">
                <FontAwesomeIcon icon={faBolt} className="h-4 w-4 text-slate-900" />
              </span>
              <div className="leading-tight">
                <p className="text-xs font-black uppercase tracking-wide text-slate-900">Cek stok</p>
                <p className="text-[11px] text-slate-500">Konfirmasi melalui WhatsApp</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <WhatsAppChannelSection showSocksPromo={false} />

      {/* Brand-protection notice — Dropify only takes orders via WhatsApp, not marketplaces */}
      <section className="bg-white pt-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-5 py-4">
            <FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500" />
            <p className="text-base leading-relaxed text-slate-700">
              <span className="font-bold text-slate-900">Pemesanan resmi Dropify dilakukan melalui WhatsApp.</span> Dropify tidak membuka toko di Tokopedia, Shopee, Lazada, atau marketplace lain. Pastikan nomor tujuan sesuai dengan kontak yang tercantum di website ini.
            </p>
          </div>
        </div>
      </section>

      {/* Who it's for — let the visitor self-identify */}
      <section className="py-14 bg-white border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4">
          <div className="mb-9 max-w-2xl">
            <h2 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">Pembeli yang dilayani halaman ini</h2>
            <p className="mt-2 text-base text-slate-500">Informasi berikut ditujukan untuk reseller dan toko yang membeli sarung tangan dalam jumlah grosir.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3 md:gap-4">
            {FIT_ITEMS.map((item) => (
              <div key={item} className="flex items-start gap-3 rounded-2xl border border-slate-100 bg-slate-50 p-4">
                <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-green-700">
                  <FontAwesomeIcon icon={faCheck} className="h-3 w-3 text-white" />
                </span>
                <p className="text-base leading-relaxed text-slate-700">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why us */}
      <section className="py-14 bg-white border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4">
          <div className="mb-9 max-w-2xl">
            <h2 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">Kenapa kulakan sarung tangan di sini?</h2>
            <p className="mt-2 text-base text-slate-500">Setiap poin menjelaskan data atau proses yang dapat diperiksa sebelum pembayaran.</p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {WHY_US_ITEMS.map((item) => (
              <div key={item.title} className="rounded-2xl border border-slate-100 bg-slate-50 p-5">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-600">
                  <FontAwesomeIcon icon={item.icon} className="h-5 w-5 text-white" />
                </div>
                <h3 className="mt-4 text-base font-black tracking-tight text-slate-900">{item.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product types showcase — built from the real photos */}
      <section className="py-14 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="mb-8 max-w-2xl">
            <h2 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">Bandingkan jenis sarung tangan</h2>
            <p className="mt-2 text-base text-slate-500">Bandingkan katun rajut, bintik PVC, dan seri berlapis. Jika produk belum terlihat, kirim nama atau foto referensinya agar admin dapat memeriksa katalog.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {PRODUCT_TYPES.map((type) => (
              <div key={type.title} className="group flex flex-col overflow-hidden rounded-2xl border border-slate-100 bg-white transition-all hover:border-blue-200 hover:shadow-lg sm:flex-row">
                <div className="relative overflow-hidden bg-white sm:w-44 sm:flex-shrink-0">
                  <img src={type.image} alt={type.title} width="400" height="400" loading="lazy" decoding="async" className="h-48 w-full object-cover transition-transform duration-500 group-hover:scale-105 sm:h-full" />
                  <span className="absolute left-3 top-3 rounded-full bg-blue-600 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-white shadow">{type.tag}</span>
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <h3 className="text-base font-black tracking-tight text-slate-900">{type.title}</h3>
                  <p className="mt-1.5 flex-1 text-base leading-relaxed text-slate-500">{type.desc}</p>
                  <WhatsAppButton
                    message={`Halo Dropify, saya tertarik ${type.title}. Mohon cek informasi stok, MOQ, dan quotation untuk jumlah ___ lusin ke ___.`}
                    label="Periksa stok dan quotation"
                    itemName={type.title}
                    className="mt-4 inline-flex w-fit items-center gap-1.5 rounded-lg bg-green-700 px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-green-800"
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Trust badges */}
          <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-3">
            {[
              { icon: faShieldHalved, text: 'Marking dan dokumen diperiksa per seri Gekko Grip' },
              { icon: faHelmetSafety, text: 'Produk dipilih sesuai pekerjaan dan risikonya' },
              { icon: faTruck, text: 'Pengiriman dari Surabaya ke berbagai kota' },
            ].map((b) => (
              <div key={b.text} className="flex items-center gap-3 rounded-2xl border border-slate-100 bg-white p-4">
                <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                  <FontAwesomeIcon icon={b.icon} className="h-4 w-4" />
                </span>
                <p className="text-sm font-medium leading-snug text-slate-600">{b.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Scalev-style wall of proof */}
      <TestimonialWall images={testimonialImages} />

      {/* Objection handling — catch the unsure visitor */}
      <section className="py-14 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="mb-9 max-w-2xl">
            <h2 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">Hal yang perlu dipastikan sebelum order</h2>
            <p className="mt-2 text-base text-slate-500">Jawaban berikut menjelaskan MOQ, sample, satuan pembelian, dan data yang perlu dikirim kepada admin.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {OBJECTIONS.map((item) => (
              <div key={item.worry} className="rounded-2xl border border-slate-100 bg-slate-50 p-5">
                <p className="text-base font-bold text-slate-900">{item.worry}</p>
                <div className="mt-2 flex gap-2.5">
                  <FontAwesomeIcon icon={faCheck} className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-600" />
                  <p className="text-base leading-relaxed text-slate-600">{item.answer}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 rounded-2xl bg-blue-50 border border-blue-100 px-6 py-6 text-center sm:text-left">
            <p className="text-base font-semibold text-slate-700">Kirim pertanyaan Anda kepada admin sebelum menentukan pesanan.</p>
            <WhatsAppButton
              message={softMessage}
              label="Tanyakan kepada admin"
              placement="objection_section"
              className="inline-flex flex-shrink-0 items-center gap-2 rounded-lg bg-green-700 hover:bg-green-800 px-5 py-3 text-sm font-bold text-white transition-colors"
            />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <LpFaqSection faqItems={FAQ_ITEMS} />

      {/* Final CTA */}
      <section className="bg-[#0B1120] py-16">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-black tracking-tight text-white">Minta quotation sarung tangan grosir</h2>
          <p className="mx-auto mt-3 max-w-xl text-base text-slate-300">
            Kirim jenis sarung tangan, jumlah, dan kota tujuan. Admin akan memeriksa stok serta menyiapkan quotation sebelum Anda memesan.
          </p>
          <div className="mt-7 flex justify-center">
            <WhatsAppButton
              message={ctaMessage}
              label="Minta quotation via WhatsApp"
              placement="closing"
              className="inline-flex items-center gap-2.5 rounded-lg bg-green-700 hover:bg-green-800 px-7 py-4 text-base font-bold text-white shadow-lg shadow-green-900/30 transition-colors"
            />
          </div>
          <p className="mt-4 text-sm text-amber-300/90">
            <FontAwesomeIcon icon={faBolt} className="w-3 h-3 mr-1" />
            Pastikan kode produk, satuan, dan jumlah pada quotation sudah sesuai.
          </p>

          {/* Legitimacy: accepted payment methods, reassures cold buyers */}
          <div className="mt-9 flex flex-col items-center gap-3 border-t border-white/10 pt-8">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Menerima pembayaran</p>
            <div className="flex items-center gap-2.5">
              <div className="flex items-center justify-center rounded-lg bg-white px-3" style={{ height: 40 }}>
                <img src="/bca.svg" alt="BCA" width="72" height="22" loading="lazy" decoding="async" className="h-5 w-auto object-contain" />
              </div>
              <div className="flex items-center justify-center rounded-lg bg-white px-3" style={{ height: 40 }}>
                <img src="/mandiri.png" alt="Mandiri" width="80" height="22" loading="lazy" decoding="async" className="h-5 w-auto object-contain" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cross-links to sibling landing pages */}
      <LpCrossLinks currentPath="/lp/grosir-sarung-tangan" />
      <AdNewsProofSection posts={newsPosts} />
      </main>

      {/* Footer with legitimacy + contact (no nav links — keeps the ad funnel tight) */}
      <footer className="border-t border-slate-100 bg-white py-10">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="inline-flex items-center gap-2 text-sm font-black tracking-tight text-slate-900">
            <FontAwesomeIcon icon={faBuilding} className="h-4 w-4 text-blue-600" />
            PT Dropify Distribusi Indonesia
          </p>
          <p className="mt-1.5 text-xs text-slate-500">Pemesanan grosir melalui WhatsApp</p>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-slate-600">
            <a
              href={`https://wa.me/${contactPhone.replace(/[^0-9]/g, '')}`}
              {...whatsAppCtaProps('landing_page', { cta_placement: 'footer_phone', lp_category: PRODUCT_NAME })}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 hover:text-green-700 transition-colors"
            >
              <FontAwesomeIcon icon={faWhatsapp} className="h-3.5 w-3.5 text-green-600" />
              {contactPhone}
            </a>
            <a href={`mailto:${contactEmail}`} className="inline-flex items-center gap-1.5 hover:text-blue-700 transition-colors">
              <FontAwesomeIcon icon={faEnvelope} className="h-3.5 w-3.5 text-blue-600" />
              {contactEmail}
            </a>
          </div>
          <div className="mt-5 flex justify-center">
            <SocialLinks variant="light" />
          </div>
          <p className="mt-5 text-xs text-slate-500">© Dropify. Semua hak cipta dilindungi.</p>
          <BrandTrademark className="mt-1 text-xs text-slate-500" />
        </div>
      </footer>
    </div>
  )
}

function TestimonialWall({ images }: { images: string[] }) {
  if (images.length === 0) return null

  const columns: string[][] = [[], [], []]
  images.forEach((src, i) => columns[i % 3].push(src))
  const durations = ['38s', '48s', '42s']
  const directions = ['lp-up', 'lp-down', 'lp-up']

  return (
    <section className="relative overflow-hidden bg-slate-50 py-14">
      <style>{`
        @keyframes lp-up { from { transform: translateY(0) } to { transform: translateY(-50%) } }
        @keyframes lp-down { from { transform: translateY(-50%) } to { transform: translateY(0) } }
        .lp-track { animation: lp-up var(--dur,40s) linear infinite; }
        .lp-track.lp-down { animation-name: lp-down; }
        .lp-wall:hover .lp-track { animation-play-state: paused; }
        @media (prefers-reduced-motion: reduce) { .lp-track { animation: none; } }
      `}</style>

      <div className="max-w-6xl mx-auto px-4">
        <div className="mb-8 max-w-2xl">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 px-3 py-1 text-xs font-bold uppercase tracking-wider text-green-700">
            <FontAwesomeIcon icon={faCheck} className="h-3 w-3" /> Dokumentasi layanan Dropify
          </span>
          <h2 className="mt-4 text-2xl md:text-3xl font-black tracking-tight text-slate-900">Dokumentasi pesanan grosir yang telah kami tangani</h2>
          <p className="mt-2 text-base text-slate-500">Dokumentasi ini berasal dari pemesanan, penyiapan barang, dan pengiriman beberapa kategori Dropify.</p>
        </div>
      </div>

      <div className="lp-wall relative mx-auto max-w-6xl px-4">
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {columns.map((column, ci) => (
            <div key={ci} className={`h-[460px] sm:h-[560px] overflow-hidden ${ci === 2 ? 'hidden lg:block' : ''}`}>
              <div className={`flex flex-col gap-4 lp-track ${directions[ci] === 'lp-down' ? 'lp-down' : ''}`} style={{ '--dur': durations[ci] } as CSSProperties}>
                {[...column, ...column].map((src, i) => (
                  <WallCard key={`${ci}-${i}`} src={toExternalImage(src, LP_WALL_IMAGE_TRANSFORM)} />
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="pointer-events-none absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-slate-50 to-transparent" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-slate-50 to-transparent" />
      </div>
    </section>
  )
}

function WallCard({ src }: { src: string }) {
  return (
    <figure className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <img src={src} alt="Dokumentasi pemesanan dan pengiriman Dropify" width="380" height="507" loading="lazy" decoding="async" className="block w-full aspect-[3/4] object-cover" />
    </figure>
  )
}

function LpFaqSection({ faqItems }: { faqItems: FaqItem[] }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  return (
    <section className="bg-slate-50 py-14">
      <div className="max-w-3xl mx-auto px-4">
        <div className="mb-8">
          <h2 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">Pertanyaan seputar sarung tangan grosir</h2>
          <p className="mt-2 text-base text-slate-500">Kirim pertanyaan yang belum terjawab kepada admin melalui WhatsApp.</p>
        </div>
        <div className="space-y-2">
          {faqItems.map((item, i) => (
            <div key={i} className="overflow-hidden rounded-xl border border-slate-200 bg-white">
              <button
                type="button"
                onClick={() => {
                  if (openIndex !== i) track('faq_open', { faq_question: item.q, faq_location: 'lp' })
                  setOpenIndex(openIndex === i ? null : i)
                }}
                aria-expanded={openIndex === i}
                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left text-base font-semibold text-slate-800 transition-colors hover:text-blue-600"
              >
                <span>{item.q}</span>
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className={`h-4 w-4 flex-shrink-0 transition-transform duration-200 ${openIndex === i ? 'rotate-180 text-blue-600' : 'text-slate-400'}`}
                />
              </button>
              {openIndex === i && (
                <div className="border-t border-slate-100 px-5 pb-4 pt-3 text-base leading-relaxed text-slate-600">
                  {item.a}
                  {item.link && (
                    <a href={item.link.href} className="mt-2 block font-semibold text-blue-600 hover:text-blue-700">
                      {item.link.label}
                    </a>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
